import { useState } from "react";
import { toast } from "sonner";
import { Pencil, PackageCheck, Tag, Undo2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { History } from "./History";
import { StatusBadge } from "./StatusBadge";
import { PartsList } from "./PartsEditor";
import { Attachments } from "./Attachments";

export const ShelfDetailDialog = ({ car, onOpenChange, onEdit, onDispatched, onLabel }) => {
  const { t, fmt } = useI18n();
  const [step, setStep] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("all");
  const [picked, setPicked] = useState([]);
  if (!car) return null;
  const pending = car.parts.filter((p) => !p.delivered);

  const isReturn = step === "return";
  const close = (v) => { if (!v) { setStep(null); setRecipient(""); setNote(""); setReason(""); setMode("all"); setPicked([]); } onOpenChange(v); };
  const toggle = (id) => setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const dispatch = async () => {
    if (mode === "parts" && picked.length === 0) return toast.error(t("selectParts"));
    setBusy(true);
    try {
      const { data } = await api.post(`/cars/${car.id}/${isReturn ? "return" : "dispatch"}`, { recipient_name: recipient, note, mode, part_ids: picked, reason: isReturn ? reason : null });
      toast.success(t("success"));
      close(false);
      onDispatched(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={!!car} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="shelf-detail-dialog">
        <DialogHeader><DialogTitle>{t("shelf")} {car.shelf_number}</DialogTitle></DialogHeader>
        <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 space-y-2">
          <span className="plate" data-testid="detail-plate">{car.plate_number}</span>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><div className="text-slate-500 text-xs">{t("carType")}</div><div className="font-bold" data-testid="detail-car-type">{car.car_type}</div></div>
            <div><div className="text-slate-500 text-xs">{t("supplier")}</div><div className="font-bold" data-testid="detail-supplier">{car.supplier_name}</div></div>
            <div><div className="text-slate-500 text-xs">{t("status")}</div><StatusBadge status={car.status} testId="detail-status" /></div>
            <div><div className="text-slate-500 text-xs">{t("date")}</div><div className="font-bold" data-testid="detail-status-date">{fmt(car.status_date)}</div></div>
          </div>
          <PartsList parts={car.parts} testId="detail-parts" showStatus />
        </div>
        {!step ? (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" data-testid="edit-car-btn" onClick={() => onEdit(car)}><Pencil className="w-4 h-4" />{t("editCar")}</Button>
            <Button variant="outline" data-testid="print-label-btn" onClick={() => onLabel(car)}><Tag className="w-4 h-4" />{t("printLabel")}</Button>
            <Button variant="outline" className="border-orange-300 text-orange-800 hover:bg-orange-50" data-testid="return-car-btn" onClick={() => setStep("return")}><Undo2 className="w-4 h-4" />{t("returnToSupplier")}</Button>
            <Button className="bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]" data-testid="dispatch-car-btn" onClick={() => setStep("dispatch")}><PackageCheck className="w-4 h-4" />{t("dispatch")}</Button>
          </div>
        ) : (
          <div className={`space-y-3 border rounded-lg p-4 ${isReturn ? "bg-orange-50 border-orange-300" : "bg-slate-50"}`}>
            <div className="font-bold text-sm" data-testid="release-step-title">{isReturn ? t("returnToSupplier") : t("dispatch")}</div>
            {pending.length > 0 && (
              <div className="space-y-2" data-testid="dispatch-mode">
                {[["all", isReturn ? "returnModeAll" : "modeAll"], ["parts", isReturn ? "returnModeParts" : "modeParts"]].map(([m, l]) => (
                  <label key={m} className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm font-semibold transition-colors ${mode === m ? "border-slate-900 bg-white" : "bg-slate-100 hover:bg-white"}`}>
                    <input type="radio" name="dispatch-mode" data-testid={`dispatch-mode-${m}`} checked={mode === m} onChange={() => setMode(m)} />{t(l)}
                  </label>
                ))}
                {mode === "parts" && (
                  <div className="bg-white border rounded-md p-2 space-y-1" data-testid="dispatch-parts-list">
                    {pending.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" data-testid={`dispatch-part-${p.id}`} checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                        <span className="font-semibold flex-1">{p.name}{p.part_number && <span className="font-mono text-xs text-slate-500 ms-2" dir="ltr">#{p.part_number}</span>}</span>
                        <span className="font-mono font-bold">× {p.quantity}</span>
                      </label>
                    ))}
                    <p className="text-xs text-slate-500 px-1">{t("partsStayHint")}</p>
                  </div>
                )}
              </div>
            )}
            {isReturn && <div className="space-y-1.5"><Label>{t("returnReason")}</Label><Input data-testid="return-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>}
            <div className="space-y-1.5"><Label>{isReturn ? t("supplierRep") : t("recipient")}</Label><Input data-testid="dispatch-recipient-input" value={recipient} onChange={(e) => setRecipient(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>{t("note")}</Label><Input data-testid="dispatch-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setStep(null)}>{t("cancel")}</Button>
              <Button disabled={busy} className={isReturn ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]"} data-testid="confirm-dispatch-btn" onClick={dispatch}>{isReturn ? t("confirmReturn") : t("confirmDispatch")}</Button>
            </div>
          </div>
        )}
        <Attachments carId={car.id} voucherNumber="invoices" title={t("invoices")} testId="car-invoices-section" />
        <div><h4 className="font-bold mb-3">{t("history")}</h4><History items={car.history} /></div>
      </DialogContent>
    </Dialog>
  );
};
